// Week time card — a week-at-a-glance heatmap of when each person was
// genuinely at the machine, built from the already-synced hourly session data.
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Loader2, CalendarDays } from "lucide-react";
import { DayBandChart, TimelineLegend, CLS_COLOR } from "@/components/prodTimelineShared";

const addDays = (iso, n) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};
const prettyDay = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" });
const prettyFull = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
const hourLabel = (h) => `${h % 12 || 12}${h < 12 ? "a" : "p"}`;
const hourFull = (h) => `${h % 12 || 12} ${h < 12 ? "AM" : "PM"}`;
const CELL_W = 9; // px per hour
const WEEKEND = ["Sat", "Sun"];

// emerald that deepens with how much of the hour was active
const shade = (sec) => {
  if (!sec || sec <= 0) return null;
  const a = 0.18 + 0.82 * Math.min(1, sec / 3600);
  return `rgba(16,185,129,${a.toFixed(2)})`;
};

// One person's full-day drill-down (band chart + longest break + top apps).
function DayDetailDialog({ target, onClose }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!target) return;
    setLoading(true); setD(null);
    api.get(`/productivity/users/${target.key}/day?date=${target.date}`)
      .then(({ data }) => setD(data))
      .catch(() => toast.error("Could not load that day"))
      .finally(() => setLoading(false));
  }, [target]);
  const li = d?.longest_idle;
  return (
    <Dialog open={!!target} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl" data-testid="prod-timecard-day-modal">
        <DialogHeader>
          <DialogTitle>{target?.name} · {target ? prettyFull(target.date) : ""}</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center py-12 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : !d ? (
          <p className="py-8 text-center text-sm text-slate-400">No data for this day.</p>
        ) : (
          <div className="space-y-4">
            {d.bands && d.bands.length > 0 ? (
              <>
                <TimelineLegend />
                <DayBandChart bands={d.bands} window={d.band_window} testid="prod-timecard-day-bandchart" />
              </>
            ) : <p className="text-sm text-slate-400">No activity recorded this day.</p>}
            {li && li.seconds >= 900 && (
              <div className="text-xs text-amber-600 dark:text-amber-400" data-testid="prod-timecard-day-break">
                Longest break {li.hms} @ {li.start}
              </div>
            )}
            {d.apps && d.apps.length > 0 && (
              <div>
                <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Top apps &amp; sites</div>
                <div className="space-y-0.5">
                  {d.apps.slice(0, 8).map((a) => (
                    <div key={a.name} className="flex items-center gap-2 text-sm">
                      <span className="h-2.5 w-2.5 shrink-0 rounded-[2px]" style={{ background: CLS_COLOR[a.cls] || CLS_COLOR.unclassified }} />
                      <span className="min-w-0 flex-1 truncate text-slate-700 dark:text-slate-200" title={a.name}>{a.name}</span>
                      <span className="shrink-0 tabular-nums text-slate-500 dark:text-slate-400">{a.hms}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function ProdTimecard() {
  const [week, setWeek] = useState(null);      // week param sent to the API (null = current)
  const [data, setData] = useState(null);
  const [thisWeek, setThisWeek] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWeekends, setShowWeekends] = useState(false);
  const [dayTarget, setDayTarget] = useState(null);

  const load = useCallback(async (wk) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/productivity/timecard${wk ? `?week=${wk}` : ""}`);
      setData(data);
      setThisWeek((prev) => (wk ? prev : data.week_start));   // first (unparam'd) load = current week
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(week); }, [week, load]);

  const hours = data ? Array.from({ length: data.hour_hi - data.hour_lo + 1 }, (_, i) => data.hour_lo + i) : [];
  const dayW = hours.length * CELL_W;
  const isThisWeek = !week || (data && data.week_start === thisWeek);
  // Weekday indices to render (weekends hidden unless toggled on).
  const visIdx = data ? data.days.map((d, i) => i).filter((i) => showWeekends || !WEEKEND.includes(data.days[i].dow)) : [];

  return (
    <div data-testid="prod-timecard" className="min-w-0 max-w-full">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => setWeek(addDays(data.week_start, -7))}
          disabled={!data || loading} data-testid="prod-timecard-prev" aria-label="Previous week">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-[190px] text-center text-sm font-semibold text-slate-800 dark:text-slate-100"
          data-testid="prod-timecard-week">
          {data ? <>Week of {prettyDay(data.week_start)} – {prettyDay(addDays(data.week_start, 6))}
            {isThisWeek && <span className="ml-1 font-normal text-emerald-600 dark:text-emerald-400">· This week</span>}</> : "…"}
        </div>
        <Button variant="outline" size="sm" onClick={() => setWeek(addDays(data.week_start, 7))}
          disabled={!data || loading || isThisWeek} data-testid="prod-timecard-next" aria-label="Next week">
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setWeek(null)} disabled={isThisWeek || loading}
          data-testid="prod-timecard-thisweek">
          <CalendarDays className="mr-1 h-3.5 w-3.5" /> This week
        </Button>
        <label className="ml-2 flex cursor-pointer items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300">
          <Switch checked={showWeekends} onCheckedChange={setShowWeekends} data-testid="prod-timecard-weekends" />
          Weekends
        </label>
        <div className="ml-auto flex items-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-400">
          <span>Less</span>
          {[0, 900, 1800, 2700, 3600].map((s) => (
            <span key={s} className="h-3 w-3 rounded-[2px] border border-slate-200 dark:border-slate-700"
              style={{ background: shade(s) || "transparent" }} />
          ))}
          <span>More · each cell = 1 hour of active time</span>
        </div>
      </div>

      <Card className="max-w-full overflow-x-auto p-3">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : !data || data.people.length === 0 ? (
          <div className="py-16 text-center text-sm text-slate-400" data-testid="prod-timecard-empty">
            No recorded activity for this week.
          </div>
        ) : (
          <div className="min-w-max">
            {/* header: day labels */}
            <div className="flex items-end border-b border-slate-200 pb-1 dark:border-slate-700">
              <div className="w-44 shrink-0 pl-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Person</div>
              <div className="flex gap-2">
                {visIdx.map((i) => {
                  const d = data.days[i];
                  return (
                    <div key={d.date} style={{ width: dayW }} className="text-center">
                      <div className="text-xs font-semibold text-slate-700 dark:text-slate-200">{d.dow}</div>
                      <div className="text-[10px] text-slate-400">{prettyDay(d.date)}</div>
                      <div className="mt-0.5 flex justify-between text-[8px] leading-none text-slate-300 dark:text-slate-600">
                        <span>{hourLabel(data.hour_lo)}</span><span>{hourLabel(data.hour_hi)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="w-20 shrink-0 pr-1 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-400">Week</div>
            </div>

            {/* one row per person */}
            {data.people.map((p) => (
              <div key={p.key} className="flex items-center border-b border-slate-100 py-1.5 last:border-0 dark:border-slate-800"
                data-testid={`prod-timecard-row-${p.key}`}>
                <div className="w-44 shrink-0 pl-1">
                  <div className="truncate text-sm font-medium text-slate-800 dark:text-slate-100" title={p.name}>{p.name}</div>
                  <div className="truncate text-[10px] text-slate-400">{p.team}</div>
                </div>
                <div className="flex gap-2">
                  {visIdx.map((i) => {
                    const cell = p.days[i];
                    const active = cell.active_seconds > 0;
                    return (
                      <button key={cell.date} type="button" style={{ width: dayW }}
                        onClick={active ? () => setDayTarget({ key: p.key, name: p.name, date: cell.date }) : undefined}
                        disabled={!active}
                        data-testid={`prod-timecard-cell-${p.key}-${cell.date}`}
                        className={`flex overflow-hidden rounded-[3px] ring-1 ring-slate-200/70 dark:ring-slate-700/70 ${active ? "cursor-pointer transition-shadow hover:ring-2 hover:ring-emerald-400" : "cursor-default"}`}
                        title={active
                          ? `${new Date(`${cell.date}T00:00:00`).toLocaleDateString(undefined, { weekday: "short" })} ${prettyDay(cell.date)} · ${cell.active_hms}${cell.start ? ` · ${cell.start}–${cell.end}` : ""} · click for detail`
                          : `${prettyDay(cell.date)} · no activity`}>
                        {hours.map((h) => {
                          const sec = cell.hours[String(h)] || 0;
                          const bg = shade(sec);
                          return (
                            <div key={h} style={{ width: CELL_W, background: bg || undefined }}
                              className={`h-6 ${bg ? "" : "bg-slate-100 dark:bg-slate-800/60"}`} />
                          );
                        })}
                      </button>
                    );
                  })}
                </div>
                <div className="w-20 shrink-0 pr-1 text-right text-sm font-semibold tabular-nums text-slate-700 dark:text-slate-200"
                  data-testid={`prod-timecard-total-${p.key}`}>{p.week_total_hms}</div>
              </div>
            ))}
          </div>
        )}
      </Card>
      <p className="mt-2 text-[11px] text-slate-400 dark:text-slate-500">
        Hours shown: {data ? `${hourFull(data.hour_lo)} – ${hourFull(data.hour_hi)}` : "—"} (America/Edmonton).
        Colour depth = active time in that hour; blank hours mean away from the machine. Click a day for the full band chart.
      </p>
      <DayDetailDialog target={dayTarget} onClose={() => setDayTarget(null)} />
    </div>
  );
}
